import BlockUi from '@availity/block-ui';
import React from 'react';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import { DateInput, TimeInput } from 'semantic-ui-calendar-react-17';
import { Checkbox, Divider, List, Message, Segment } from 'semantic-ui-react';
import AzureMapFromContext from '../shared/AzureMap/AzureMapFromContext';
import { BusPositionHistoryContext } from './BusHistoryContext';
import BusHistorySchedule from './BusHistorySchedule';
import { DirectionsDropdown, RouteDropdown, StopsDropdown } from './BusHistorySelectors';
import BusHistorySlider from './BusHistorySlider';
import BusHistorySliderInfo from './BusHistorySliderInfo';
import DirectionInfo from './DirectionInfo';
import { AgencyModel } from './types/agencyModel';
import { BphDirectionModel, BphRouteModel, BphStopModel, BusHistoryUrlFilterKey, BusHistoryUrlFilters, ParsedBusHistoryUrlFilters } from './types/BphTypes';
import { toBusHistoryUrlSearch } from './urlFilters';

const styles: { [key: string]: React.CSSProperties } = {
    date: {
        display: 'inline-block',
        width: '100%',
    },
    time: {
        display: 'inline-block',
        width: '100%',
    },
    navLink: {
        color: '#337ab7',
        float: 'right',
        fontFamily: '"Segoe UI",Verdana,Helvetica,Sans-Serif',
    },
};

function simpleDebounce(callback: (text: string) => void, delay: number) {
    let timer: number;
    return (text: string) => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => callback(text), delay);
    };
}

interface Props {
    agency: AgencyModel;
    initialUrlFilters?: ParsedBusHistoryUrlFilters;
}

const urlFilterLabels: { [key in BusHistoryUrlFilterKey]: string } = {
    date: 'date',
    time: 'time',
    routeId: 'routeId',
    stopId: 'stopId',
    directionVariantId: 'directionVariantId',
    displayStops: 'displayStops',
};

export default function BusHistoryPane(props: Props) {
    const context = React.useContext(BusPositionHistoryContext);
    const history = useHistory();
    const location = useLocation();
    const urlFiltersInitialized = React.useRef<boolean>(false);
    const initialDateTimeRef = React.useRef<{ date: string, time: string } | null>(null);

    const [searchText, setSearchText] = React.useState('');
    const debounceSearch = React.useCallback(
        simpleDebounce((text: string) => {
            context.setStopsSearchString(text);
        }, 1000),
        [],
    );

    React.useEffect(() => {
        if (urlFiltersInitialized.current) return;
        context.initializeUrlFilters(props.initialUrlFilters);
        urlFiltersInitialized.current = true;
    }, [props.initialUrlFilters]);

    React.useEffect(() => {
        if (initialDateTimeRef.current) {
            return;
        }
        initialDateTimeRef.current = {
            date: context.dateTime.selectedDate,
            time: context.dateTime.selectedTime,
        };
    }, [context.dateTime.selectedDate, context.dateTime.selectedTime]);

    React.useEffect(() => {
        context.setAgency(props.agency);
    }, [props.agency]);

    React.useEffect(() => {
        debounceSearch(searchText);
    }, [searchText]);

    React.useEffect(() => {
        if (!context.urlFiltersState.initialized || context.urlFiltersState.applying) {
            return;
        }

        const hasPrimarySelection: boolean = !!context.routesState.selectedRoute
            || !!context.stopsState.selectedStop
            || !!context.directionsState.selectedDirection;

        const sourceFilters: Nullable<BusHistoryUrlFilters> = context.urlFiltersState.sourceFilters;
        const initialDateTime = initialDateTimeRef.current;
        const hasDateTimeOverride: boolean = !!initialDateTime
            && (context.dateTime.selectedDate !== initialDateTime.date || context.dateTime.selectedTime !== initialDateTime.time);
        const includeDateTime: boolean = hasPrimarySelection || hasDateTimeOverride || !!sourceFilters?.date || !!sourceFilters?.time;

        const filters: BusHistoryUrlFilters = {};
        if (includeDateTime) {
            filters.date = context.dateTime.selectedDate;
            filters.time = context.dateTime.selectedTime;
        }
        if (context.routesState.selectedRoute) {
            filters.routeId = context.routesState.selectedRoute.routeId;
        }
        if (context.stopsState.selectedStop) {
            filters.stopId = context.stopsState.selectedStop.stopId;
        }
        if (context.directionsState.selectedDirection) {
            filters.directionVariantId = context.directionsState.selectedDirection.directionVariantId;
            filters.displayStops = context.displayDirectionStops;
        }

        const nextSearch: string = toBusHistoryUrlSearch(filters);
        if (nextSearch === location.search) {
            return;
        }

        history.replace({
            pathname: location.pathname,
            search: nextSearch,
        });
    }, [
        context.urlFiltersState.initialized,
        context.urlFiltersState.applying,
        context.urlFiltersState.sourceFilters,
        context.dateTime.selectedDate,
        context.dateTime.selectedTime,
        context.routesState.selectedRoute,
        context.stopsState.selectedStop,
        context.directionsState.selectedDirection,
        context.displayDirectionStops,
        history,
        location.pathname,
        location.search,
    ]);

    // const mapDiv = React.useRef<HTMLDivElement | null>(null);
    // React.useEffect(() => {
    //     if (mapDiv.current === null) return;
    //     context.onMapReady(mapDiv.current);
    // }, [mapDiv.current]);

    // const mapRef = React.useRef<any>(null);
    // React.useEffect(() => {
    //     if (mapRef.current === null) return;
    //     context.onMapReady(mapRef.current);
    // }, [mapRef.current]);

    const handleDateChange = (_event: React.SyntheticEvent<HTMLElement, Event>, { value }: { value: string }) => {
        context.updateDateTime(value, null);
    };
    const handleTimeChange = (_event: React.SyntheticEvent<HTMLElement, Event>, { value }: { value: string }) => {
        context.updateDateTime(null, value);
    };
    const onRouteSelected = (route: BphRouteModel | null) => {
        context.selectRoute(route);
    };
    const handleStopChanged = (value: BphStopModel | null) => {
        context.selectStop(value);
    };
    const handleDirectionChanged = (value: BphDirectionModel | null) => {
        context.selectDirection(value);
    };
    const handleSearchChanged = (searchQuery: string) => {
        setSearchText(searchQuery);
    };

    const droppedFilterLabels: string[] = context.urlFiltersState.droppedKeys.map((key: BusHistoryUrlFilterKey) => urlFilterLabels[key]);
    const showUrlWarning: boolean = context.urlFiltersState.initialized
        && context.urlFiltersState.stage === 'completed'
        && droppedFilterLabels.length > 0;

    if (!props.agency) return null;

    return (
        <div className="bushistory-container">
            <BlockUi blocking={context.routesLoading || context.directionsLoading || context.historyLoading}>
                <Segment className="content-pane">
                    <div className="ctl-pane">
                        <div className="pane1">
                            <List className="sidebarlist">
                                <List.Item>
                                    <NavLink to="/bushistorybybusnumber" style={styles.navLink}> Look up by bus number...</NavLink>
                                </List.Item>
                                {showUrlWarning
                                    ? <List.Item>
                                        <Message warning size="tiny">
                                            Ignored URL parameters: {droppedFilterLabels.join(', ')}.
                                        </Message>
                                    </List.Item>
                                    : null
                                }
                                <List.Item>
                                    <DateInput className="bph-date"
                                        name="busHistoryDate"
                                        dateFormat="YYYY-MM-DD"
                                        placeholder="Select date"
                                        value={context.dateTime.selectedDate}
                                        iconPosition="left"
                                        popupPosition="bottom center"
                                        closable={true}
                                        startMode="day"
                                        preserveViewMode={false}
                                        animation="fade"
                                        disableMinute={false}
                                        style={styles.date}
                                        onChange={handleDateChange}
                                        maxDate={new Date()}
                                    />
                                    <TimeInput className="bph-time"
                                        name="busHistoryTime"
                                        timeFormat="AMPM"
                                        placeholder="Select day time"
                                        value={context.dateTime.selectedTime}
                                        onChange={handleTimeChange}
                                        iconPosition="left"
                                        popupPosition="bottom center"
                                        closable={true}
                                        animation="fade"
                                        style={styles.time}
                                    />
                                </List.Item>
                                {context.routesLoading
                                    ? null
                                    : <>
                                        <List.Item>
                                            <RouteDropdown
                                                items={context.routesState.routes}
                                                value={context.routesState.selectedRoute}
                                                onChanged={onRouteSelected}
                                            />
                                        </List.Item>
                                        {!context.routesState.routes
                                            ? null
                                            : <>
                                                <List.Item>
                                                    <StopsDropdown
                                                        loading={context.stopsLoading}
                                                        items={context.stopsState.stops}
                                                        value={context.stopsState.selectedStop}
                                                        onChanged={handleStopChanged}
                                                        noResultsMessage={context.stopsNoResultMessageState}
                                                        onSearchChanged={handleSearchChanged}
                                                    />
                                                </List.Item>
                                                <Divider />
                                                <List.Item>
                                                    {context.directionsLoading || (!context.routesState.selectedRoute && !context.stopsState.selectedStop)
                                                        ? null :
                                                        <DirectionsDropdown
                                                            loading={context.directionsLoading}
                                                            items={context.directionsState.directions}
                                                            value={context.directionsState.selectedDirection}
                                                            selectedStopId={context.stopsState?.selectedStop ? context.stopsState?.selectedStop!.stopId : ''}
                                                            onChanged={handleDirectionChanged}
                                                        />
                                                    }
                                                </List.Item>
                                                <List.Item>
                                                    {!context.directionsState.selectedDirection
                                                        ? null :
                                                        <Checkbox
                                                            label="Display Stops"
                                                            checked={context.displayDirectionStops}
                                                            onChange={(_e, { checked }) => context.onDisplayDirectionStopsChanged(Boolean(checked))}
                                                        />
                                                    }
                                                </List.Item>
                                            </>
                                        }
                                    </>
                                }
                                {context.historyLoading
                                    ? null :
                                    context.sliderOptions &&
                                    <List.Item>
                                        <BusHistorySlider
                                            maxValue={context.sliderOptions.maxValue}
                                            sliderValue={context.sliderOptions.sliderValue}
                                            handleSliderChange={v => context.onSliderTimeChanged(v)}
                                        />
                                    </List.Item>
                                }
                            </List>
                        </div>
                        <div className="pane2">
                            {context.busPositionsState.length === 0 ?
                                context.directionsInfo.length > 0 &&
                                <DirectionInfo directions={context.directionsInfo} />
                                : !context.sliderInfo
                                    ? null :
                                    <BusHistorySliderInfo {...context.sliderInfo} />
                            }
                        </div>
                    </div>
                    {!context.stopsState.selectedStop || !context.directionsState.selectedDirection
                        ? null :
                        <div className="inf-pane">
                            <div>
                                <BusHistorySchedule />
                            </div>
                        </div>
                    }
                    <div className="map-pane">
                        <AzureMapFromContext />
                    </div>
                </Segment>
            </BlockUi>
        </div>
    );
}
